import type { AdminMenuItem, TenantRemoteMenuItem } from '@/mf/types';

export type { AdminMenuItem } from '@/mf/types';

const ADMIN_RESOURCE_MENU_LEAF_KEYS: Record<string, string> = {
  '/admin/resource/record': 'Admin:assets.menu.recordingManagement',
  '/admin/resource/school-exam/questions': 'Admin:assets.menu.schoolQuestionManagement',
  '/admin/resource/school-resource': 'Admin:assets.menu.schoolResourceManagement',
  '/admin/resource/school-resource/category': 'Admin:assets.menu.schoolResourceCategoryManagement',
  '/admin/resource/special-management': 'Admin:assets.menu.schoolTopicManagement',
};

// 运营配置已从产品下线；权限服务未清理前也不得把旧入口重新暴露给用户。
const REMOVED_ADMIN_MENU_PATHS = new Set([
  '/admin/teacher-workshop/operations-management',
]);

/** normalizePath 清理权限菜单路径中的查询、哈希与尾随斜杠。 */
function normalizePath(path: string) {
  const pathname = path.trim().split(/[?#]/, 1)[0]?.replace(/\/+$/, '') ?? '';
  if (!pathname) return '';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

const LEGACY_ADMIN_FAMILIES = [
  '/ai-resource',
  '/cms',
  '/dashboard',
  '/device-controller',
  '/evaluation',
  '/operations',
  '/resource',
  '/resouce',
  '/schedule',
  '/supervision',
  '/teacher-workshop',
];

/** normalizeELearningPath 将云研社历史命名空间统一规整到当前 yun-research 路由。 */
function normalizeELearningPath(pathname: string): string {
  const legacyPrefixes = [
    '/admin/system/yun-research',
    '/admin/yun-research',
    '/system/yun-research',
    '/yun-research',
    '/admin/e-learning',
    '/e-learning',
  ];
  for (const prefix of legacyPrefixes) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return `/admin/yun-research${pathname.slice(prefix.length)}`;
    }
  }
  return '';
}

/**
 * 将权限接口返回的旧站 system 路径映射到租户管理台命名空间。
 * 除 admin/system 外，仅放行已从旧管理台核实的顶层页面族，避免把工作台菜单误放进管理台。
 */
export function toAdminRoutePath(path = ''): string {
  const pathname = normalizePath(path);
  const eLearningPath = normalizeELearningPath(pathname);
  if (eLearningPath) return eLearningPath;
  // 权限接口仍可能返回旧版 resouce 拼写，Host 必须在菜单、选中链和面包屑计算前统一纠正。
  if (pathname === '/resouce' || pathname.startsWith('/resouce/')) {
    return `/admin/resource${pathname.slice('/resouce'.length)}`;
  }
  if (pathname === '/admin/resouce' || pathname.startsWith('/admin/resouce/')) {
    return `/admin/resource${pathname.slice('/admin/resouce'.length)}`;
  }
  if (
    pathname === '/resource/school-resouce/cateogry'
    || pathname === '/admin/resource/school-resouce/cateogry'
  ) {
    return '/admin/resource/school-resource/category';
  }
  if (pathname === '/resource/resouce' || pathname.startsWith('/resource/resouce/')) {
    return `/admin/resource/special-management${pathname.slice('/resource/resouce'.length)}`;
  }
  if (pathname === '/admin/resource/resouce' || pathname.startsWith('/admin/resource/resouce/')) {
    return `/admin/resource/special-management${pathname.slice('/admin/resource/resouce'.length)}`;
  }
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return pathname;
  if (pathname === '/system' || pathname.startsWith('/system/')) return `/admin${pathname}`;
  if (LEGACY_ADMIN_FAMILIES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return `/admin${pathname}`;
  }
  return '';
}

/** getMenuLabel 按本地标题、默认标题和路径生成稳定菜单文案。 */
function getMenuLabel(item: TenantRemoteMenuItem) {
  return item.titleLocal || item.title || item.path;
}

/** filterAdminPermissionMenus 只保留启用且属于 Admin 命名空间的权限菜单。 */
export function filterAdminPermissionMenus(items: TenantRemoteMenuItem[] = []): TenantRemoteMenuItem[] {
  return [...items]
    .filter(item => !item.disabled)
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .flatMap((item) => {
      const children = filterAdminPermissionMenus(item.children ?? []);
      const path = toAdminRoutePath(item.path);
      if (REMOVED_ADMIN_MENU_PATHS.has(path)) return [];
      const isNamespaceRoot = path === '/admin' || path === '/admin/system';
      if ((!path || isNamespaceRoot) && !children.length) return [];
      return [{ ...item, children: children.length ? children : undefined }];
    });
}

/**
 * 以 /base/iam/portal/my/menu 的返回值为唯一权限源，只保留管理台可达叶子。
 * 父级分组可以没有自身路径，但绝不在过滤为空时回退展示全部菜单。
 */
export function normalizeAdminMenus(items: TenantRemoteMenuItem[] = [], parentKey = 'admin'): AdminMenuItem[] {
  return filterAdminPermissionMenus(items)
    .flatMap((item, index) => {
      const children = normalizeAdminMenus(item.children ?? [], `${parentKey}-${index}`);
      const path = toAdminRoutePath(item.path);
      if (!path && !children.length) return [];

      const label = getMenuLabel(item) || path;
      const key = `${parentKey}-${index}-${path || label}`;
      return [{
        children: children.length ? children : undefined,
        extra: item.extra,
        icon: item.icon,
        key,
        label,
        path: path || undefined,
        sourcePath: item.path || undefined,
      }];
    });
}

/** collectAdminMenuPaths 收集当前菜单分支的规范路径，供无路径分组匹配资源空间语义。 */
function collectAdminMenuPaths(item: AdminMenuItem): string[] {
  return [
    ...(item.path ? [item.path] : []),
    ...(item.children?.flatMap(collectAdminMenuPaths) ?? []),
  ];
}

/** getAdminResourceMenuTranslationKey 为资源空间菜单返回稳定多语言键，权限标题只作兜底。 */
export function getAdminResourceMenuTranslationKey(item: AdminMenuItem): string | undefined {
  const paths = collectAdminMenuPaths(item);
  if (item.children?.length) {
    if (paths.every(path => path.startsWith('/admin/resource/school-resource'))) {
      return 'Admin:assets.menu.schoolResourceGroup';
    }
    if (paths.every(path => path.startsWith('/admin/resource/school-exam'))) {
      return 'Admin:assets.menu.schoolExamGroup';
    }
    if (paths.every(path => path === '/admin/resource' || path.startsWith('/admin/resource/'))) {
      return 'Admin:assets.menu.resourceSpace';
    }
  }
  if (item.path === '/admin/resource') return 'Admin:assets.menu.resourceSpace';
  return item.path ? ADMIN_RESOURCE_MENU_LEAF_KEYS[item.path] : undefined;
}

/** isStandaloneAdminMenu 判断顶层菜单是否应归入产品设置入口。 */
export function isStandaloneAdminMenu(item: AdminMenuItem): boolean {
  return item.extra?.isStandalone === true;
}

/**
 * 顶栏严格使用权限接口的 extra.isStandalone 分组：普通业务横排展示，部署产品收进“产品设置”。
 * 该属性属于管理台菜单协议，基座不解释也不补造默认值。
 */
export function splitAdminTopMenus(items: AdminMenuItem[]) {
  return {
    primaryMenus: items.filter(item => !isStandaloneAdminMenu(item)),
    productMenus: items.filter(isStandaloneAdminMenu),
  };
}

/** findFirstAdminMenuPath 返回权限顺序中的首个可导航叶子。 */
export function findFirstAdminMenuPath(items: AdminMenuItem[]): string {
  for (const item of items) {
    const childPath = item.children?.length ? findFirstAdminMenuPath(item.children) : '';
    if (childPath) return childPath;
    if (item.path && item.path !== '/admin') return item.path;
  }
  return '';
}

/** findAdminMenuByKey 在归一化菜单树中递归定位指定节点。 */
export function findAdminMenuByKey(items: AdminMenuItem[], key: string): AdminMenuItem | undefined {
  for (const item of items) {
    if (item.key === key) return item;
    const matched = item.children ? findAdminMenuByKey(item.children, key) : undefined;
    if (matched) return matched;
  }
  return undefined;
}

/** pathnameMatchesMenu 允许菜单叶子匹配自身及其详情子路径。 */
function pathnameMatchesMenu(pathname: string, menuPath: string) {
  return pathname === menuPath || pathname.startsWith(`${menuPath}/`);
}

/** findAdminMenuTrail 返回当前地址命中的最长权限菜单链。 */
export function findAdminMenuTrail(items: AdminMenuItem[], pathname: string): AdminMenuItem[] {
  let best: AdminMenuItem[] = [];

  for (const item of items) {
    const childTrail = item.children ? findAdminMenuTrail(item.children, pathname) : [];
    if (childTrail.length) {
      const trail = [item, ...childTrail];
      const trailPath = trail[trail.length - 1]?.path ?? '';
      const bestPath = best[best.length - 1]?.path ?? '';
      if (trailPath.length > bestPath.length) best = trail;
    }

    if (item.path && pathnameMatchesMenu(pathname, item.path)) {
      const bestPath = best[best.length - 1]?.path ?? '';
      if (item.path.length > bestPath.length) best = [item];
    }
  }

  return best;
}

/** getAdminBreadcrumbTail 为菜单未登记的详情子路由补充稳定中文语义，不把业务 ID 暴露在面包屑中。 */
export function getAdminBreadcrumbTail(pathname: string, matchedPath = ''): string[] {
  if (!matchedPath || pathname === matchedPath) return [];
  if (
    matchedPath === '/admin/resource/special-management'
    && /^\/admin\/resource\/special-management\/[^/]+\/edit\/?$/.test(pathname)
  ) {
    return ['编辑专题分类'];
  }
  return [];
}

/** findAdminGroupRedirect 将管理台根或分组地址解析到首个可访问叶子。 */
export function findAdminGroupRedirect(items: AdminMenuItem[], pathname: string): string {
  if (pathname === '/admin' || pathname === '/admin/') return findFirstAdminMenuPath(items);

  for (const item of items) {
    if (item.path === pathname && item.children?.length) {
      return findFirstAdminMenuPath(item.children);
    }
    const childRedirect = item.children ? findAdminGroupRedirect(item.children, pathname) : '';
    if (childRedirect) return childRedirect;
  }

  return '';
}
